import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, ArrowRight, Building2, Lock, Shield, HelpCircle, GraduationCap, BookOpen } from '@/components/DeskeyIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'institution', label: 'Institution', icon: Building2, needs2FA: true },
  { id: 'teacher', label: 'Teacher', icon: BookOpen, needs2FA: true },
  { id: 'student', label: 'Student', icon: GraduationCap, needs2FA: false },
];

function hexLuma(hex) {
  const c = (hex || '#000000').replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export default function TenantLoginPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState('institution');
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [devCode, setDevCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetDevCode, setResetDevCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/tenant/${slug}/config`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setConfig(d); setLoadingConfig(false); })
      .catch(() => { setNotFound(true); setLoadingConfig(false); });
  }, [slug]);

  const pc = config?.branding?.primary_color || '#34A853';
  const bg = config?.branding?.bg_color || '#0f172a';
  const logoUrl = config?.branding?.logo_url;
  const instName = config?.name || 'Institution';
  const loginTitle = config?.branding?.login_title || instName;
  const loginSub = config?.branding?.login_subtitle || 'One platform to power every classroom. Sign in to manage AI-generated lessons, track progress, and grow.';
  const footerText = config?.branding?.footer_text || `\u00a9 2025 ${instName}. All rights reserved.`;

  const dark = hexLuma(bg) < 0.5;
  const txt = dark ? '#ffffff' : '#111827';
  const sub = dark ? 'rgba(255,255,255,0.7)' : '#4b5563';
  const muted = dark ? 'rgba(255,255,255,0.4)' : '#9ca3af';
  const glass = dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)';
  const glassHi = dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)';
  const bdr = dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';

  const cur = TABS.find(t => t.id === tab);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const switchTab = (id) => { setTab(id); setMode('login'); setError(''); setSuccess(''); setForm({ email: '', password: '' }); setTwoFACode(''); setResetCode(''); setNewPassword(''); };

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tenant/${slug}/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email, password: form.password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      if (cur.needs2FA) {
        const fa = await fetch(`${API_URL}/api/auth/2fa/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email }) });
        const faD = await fa.json(); setDevCode(faD.dev_code || '');
        sessionStorage.setItem('pending_token', data.token);
        sessionStorage.setItem('pending_user', JSON.stringify(data.user));
        sessionStorage.setItem('pending_tenant', JSON.stringify(data.tenant));
        setMode('2fa');
      } else { completeLogin(data.token, data.user, data.tenant); }
    } catch (err) { setError(err.message); } setLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/2fa/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email, code: twoFACode }) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Invalid code'); }
      completeLogin(sessionStorage.getItem('pending_token'), JSON.parse(sessionStorage.getItem('pending_user')), JSON.parse(sessionStorage.getItem('pending_tenant') || 'null'));
    } catch (err) { setError(err.message); } setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/request`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email }) });
      const data = await res.json(); if (!res.ok) throw new Error(data.detail || 'Failed');
      setResetDevCode(data.dev_code || ''); setSuccess('Reset code sent to your email.'); setMode('reset-confirm');
    } catch (err) { setError(err.message); } setLoading(false);
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email, code: resetCode, new_password: newPassword }) });
      const data = await res.json(); if (!res.ok) throw new Error(data.detail || 'Failed');
      setSuccess(data.message); setTimeout(() => setMode('login'), 1500);
    } catch (err) { setError(err.message); } setLoading(false);
  };

  const completeLogin = (token, user, tenant) => {
    localStorage.setItem('desky_token', token);
    localStorage.setItem('desky_user', JSON.stringify(user));
    if (tenant) localStorage.setItem('desky_tenant', JSON.stringify(tenant));
    ['pending_token', 'pending_user', 'pending_tenant'].forEach(k => sessionStorage.removeItem(k));
    const role = user.role;
    if (role === 'tenant_owner' || role === 'tenant_admin') window.location.href = '/institution';
    else if (role === 'department_admin') window.location.href = '/department';
    else if (role === 'teacher') window.location.href = user.licence_status === 'active' ? '/teacher' : '/activate-licence';
    else if (role === 'student') window.location.href = user.licence_status === 'active' ? '/student' : '/activate-licence';
    else window.location.href = '/';
  };

  if (loadingConfig) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0f172a' }}><div className="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-white" /></div>;

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="tenant-not-found">
      <div className="text-center">
        <Building2 className="w-16 h-16 text-gray-200 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Institution Not Found</h1>
        <p className="text-sm text-gray-400 mb-6">This page doesn&apos;t exist or is inactive.</p>
        <Button onClick={() => nav('/mydeskey')} variant="outline" className="rounded-xl">Go to MyDeskey</Button>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden" data-testid="tenant-login-page">

      {/* ═══════════════════════════════════════════════════════════
          LEFT SIDE — FULL HEIGHT BRANDED PANEL
         ═══════════════════════════════════════════════════════════ */}
      <div className="hidden lg:flex lg:w-[48%] xl:w-[45%] flex-col relative" style={{ background: bg }}>
        {/* Decorative shapes */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full -translate-y-1/3 translate-x-1/3" style={{ background: glassHi }} />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full translate-y-1/3 -translate-x-1/3" style={{ background: glass }} />
        <div className="absolute top-1/2 right-12 w-28 h-28 rounded-full" style={{ background: glass }} />

        {/* Header area */}
        <div className="relative z-10 px-10 xl:px-14 pt-8">
          <div className="flex items-center gap-3">
            {logoUrl
              ? <img src={logoUrl} alt={instName} className="w-10 h-10 rounded-xl object-contain" style={{ background: glass, padding: 4 }} data-testid="tenant-logo" />
              : <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: glass }}><Building2 className="w-5 h-5" style={{ color: txt }} /></div>}
            <span className="text-base font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: txt }}>{instName}</span>
          </div>
        </div>

        {/* Main content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-10 xl:px-14">
          <h1 className="text-[3rem] xl:text-[3.6rem] font-extrabold leading-[1.05] tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif', color: txt }}>
            {loginTitle}
          </h1>

          <p className="mt-5 text-base leading-relaxed max-w-sm" style={{ color: sub }}>
            {loginSub}
          </p>

          <div className="mt-10 flex gap-8">
            {[
              { num: '10M+', label: 'Lessons' },
              { num: '500+', label: 'Institutions' },
              { num: '24/7', label: 'AI Tutor' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl font-extrabold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: txt }}>{s.num}</p>
                <p className="text-[11px] mt-1 tracking-wide uppercase" style={{ color: muted }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer area */}
        <div className="relative z-10 px-10 xl:px-14 pb-8">
          <div className="pt-6" style={{ borderTop: `1px solid ${bdr}` }}>
            <div className="flex items-center gap-2">
              <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill="#34A853"/></svg>
              <span className="text-xs font-semibold" style={{ color: txt, fontFamily: 'Space Grotesk, sans-serif' }}>Deskey</span>
              <span className="text-[10px]" style={{ color: muted }}>AI Education Platform</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          RIGHT SIDE — FORM
         ═══════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col bg-white">

        {/* Right header */}
        <div className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-gray-100">
          <div className="lg:hidden flex items-center gap-2">
            {logoUrl ? <img src={logoUrl} alt={instName} className="w-7 h-7 rounded-md object-contain" /> : <Building2 className="w-5 h-5" style={{ color: pc }} />}
            <span className="text-base font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{instName}</span>
          </div>
          <div className="hidden lg:block" />
          <a href="/mydeskey" className="text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1.5" data-testid="need-help-link">
            <HelpCircle className="w-3.5 h-3.5" /> Need Help?
          </a>
        </div>

        {/* Mobile branding bar */}
        <div className="lg:hidden px-6 py-6" style={{ background: bg }}>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: txt }}>{loginTitle}</h2>
          <p className="text-xs mt-1" style={{ color: sub }}>Sign in to your account</p>
        </div>

        {/* Form container */}
        <div className="flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-8">
          <div className="w-full max-w-[380px]" data-testid="tenant-login-card">

            {/* Role tabs */}
            {(mode === 'login' || mode === 'forgot') && (
              <div className="flex rounded-xl overflow-hidden mb-6 border border-gray-200" data-testid="tenant-role-tabs">
                {TABS.map(t => (
                  <button key={t.id} data-testid={`tenant-tab-${t.id}`} onClick={() => switchTab(t.id)}
                    className={`flex-1 py-2.5 text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${tab === t.id ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 bg-white'}`}
                    style={{ background: tab === t.id ? pc : undefined }}>
                    <t.icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                ))}
              </div>
            )}

            {/* LOGIN */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4" data-testid="tenant-login-form">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Email</label>
                  <Input data-testid="tenant-login-email" type="email" placeholder={`Enter your ${tab} email`} value={form.email} onChange={e => f('email', e.target.value)} required className="h-11 rounded-xl border-gray-200 text-sm focus-visible:ring-1" style={{ '--tw-ring-color': pc }} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Password</label>
                  <div className="relative">
                    <Input data-testid="tenant-login-password" type={show ? 'text' : 'password'} placeholder="Enter your password" value={form.password} onChange={e => f('password', e.target.value)} required className="h-11 rounded-xl border-gray-200 text-sm pr-10" />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                </div>
                {error && <p className="text-xs text-red-500" data-testid="tenant-login-error">{error}</p>}
                <Button type="submit" data-testid="tenant-login-submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold text-white text-sm shadow-lg shadow-black/5 hover:shadow-xl transition-shadow" style={{ background: pc }}>
                  {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
                {cur.needs2FA && <div className="flex items-center gap-1.5 justify-center text-[10px] text-gray-400"><Shield className="w-3 h-3" /> 2-Factor Authentication enabled</div>}
                <div className="flex justify-between items-center pt-2">
                  <button type="button" data-testid="tenant-forgot-btn" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }} className="text-xs font-medium hover:underline" style={{ color: pc }}>Forgot Password?</button>
                  <button type="button" onClick={() => nav('/mydeskey')} data-testid="tenant-signup-redirect" className="text-xs text-gray-400 hover:text-gray-600">
                    New here? <span className="font-semibold" style={{ color: pc }}>Sign Up</span>
                  </button>
                </div>
              </form>
            )}

            {/* 2FA */}
            {mode === '2fa' && (
              <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="tenant-2fa-form">
                <div className="text-center mb-2">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: `${pc}12` }}><Lock className="w-7 h-7" style={{ color: pc }} /></div>
                  <h3 className="text-lg font-bold text-gray-900">Verify Identity</h3>
                  <p className="text-xs text-gray-400 mt-1">Enter the 6-digit code sent to your email</p>
                  {devCode && <p className="text-[10px] text-orange-500 mt-1">(Dev: {devCode})</p>}
                </div>
                <Input data-testid="tenant-2fa-code" placeholder="000000" value={twoFACode} onChange={e => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))} required maxLength={6} className="h-12 rounded-xl border-gray-200 text-center text-xl tracking-[0.5em] font-mono" />
                {error && <p className="text-xs text-red-500 text-center">{error}</p>}
                <Button type="submit" disabled={loading || twoFACode.length !== 6} className="w-full h-11 rounded-xl font-semibold text-white text-sm" style={{ background: pc }} data-testid="tenant-2fa-submit">{loading ? 'Verifying...' : 'Verify & Sign In'}</Button>
                <button type="button" onClick={() => { setMode('login'); setError(''); }} className="w-full text-xs text-gray-400 hover:text-gray-600 text-center mt-2">Back to Sign In</button>
              </form>
            )}

            {/* FORGOT */}
            {mode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="tenant-forgot-form">
                <div className="text-center mb-2">
                  <h3 className="text-lg font-bold text-gray-900">Reset Password</h3>
                  <p className="text-xs text-gray-400 mt-1">We&apos;ll send a reset code to your email</p>
                </div>
                <Input data-testid="tenant-forgot-email" type="email" placeholder="Your registered email" value={form.email} onChange={e => f('email', e.target.value)} required className="h-11 rounded-xl border-gray-200 text-sm" />
                {error && <p className="text-xs text-red-500">{error}</p>}
                {success && <p className="text-xs text-green-600">{success}</p>}
                <Button type="submit" data-testid="tenant-forgot-submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold text-white text-sm" style={{ background: pc }}>{loading ? 'Sending...' : 'Send Reset Code'}</Button>
                <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }} className="w-full text-xs text-gray-400 hover:text-gray-600 text-center mt-2">Back to Sign In</button>
              </form>
            )}

            {/* RESET CONFIRM */}
            {mode === 'reset-confirm' && (
              <form onSubmit={handleResetConfirm} className="space-y-4" data-testid="tenant-reset-form">
                <div className="text-center mb-2">
                  <h3 className="text-lg font-bold text-gray-900">Enter Reset Code</h3>
                  <p className="text-xs text-gray-400 mt-1">Check your email for the 6-digit code</p>
                  {resetDevCode && <p className="text-[10px] text-orange-500 mt-1">(Dev: {resetDevCode})</p>}
                </div>
                <Input data-testid="tenant-reset-code" placeholder="6-digit code" value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required maxLength={6} className="h-11 rounded-xl border-gray-200 text-sm text-center tracking-[0.3em] font-mono" />
                <Input data-testid="tenant-reset-password" type="password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="h-11 rounded-xl border-gray-200 text-sm" />
                {error && <p className="text-xs text-red-500">{error}</p>}
                {success && <p className="text-xs text-green-600">{success}</p>}
                <Button type="submit" data-testid="tenant-reset-submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold text-white text-sm" style={{ background: pc }}>{loading ? 'Resetting...' : 'Reset Password'}</Button>
                <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }} className="w-full text-xs text-gray-400 hover:text-gray-600 text-center mt-2">Back to Sign In</button>
              </form>
            )}
          </div>
        </div>

        {/* Right footer */}
        <div className="px-6 sm:px-10 py-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-400">{footerText}</span>
            <div className="flex gap-4 text-[11px] text-gray-400">
              <a href="#" className="hover:text-gray-600 transition-colors">Privacy</a>
              <a href="#" className="hover:text-gray-600 transition-colors">Terms</a>
              <a href="/mydeskey" className="hover:text-gray-600 transition-colors">MyDeskey</a>
            </div>
          </div>
          <div className="flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-gray-50">
            <svg width="14" height="14" viewBox="0 0 40 40" fill="none"><path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill="#34A853"/></svg>
            <span className="text-[10px] text-gray-300 tracking-wide">Powered by</span>
            <span className="text-[10px] font-bold text-gray-500" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Deskey AI</span>
          </div>
        </div>
      </div>
    </div>
  );
}
